import { FC, useEffect, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer, {
  CenteredCellBox,
} from 'src/components/StyledComponents/StyledGridContainer';
import Status from 'src/components/Status';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import { useGetMaterial } from 'src/services/material.service';
import { RawMaterial } from 'src/models/Management.interface';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import RawMaterialActionMenuButton from 'src/components/ActionButtons/RawMaterialActionMenu';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import { loadData } from 'src/utils/grid.utils';

const RawMaterialGrid: FC<{
  state: boolean;
  searchTerm: string;
  categoryFilter?: string | null;
}> = ({ state, searchTerm, categoryFilter }) => {
  const { showSnackbar } = StyledSnackbar();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);
  const { loading, materials, fetchMaterials, deleteMaterial, totalItems } =
    useGetMaterial();
  const [sortBy, setSortBy] = useState<string>('name');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'createdAt', sort: 'asc' },
  ]);

  useEffect(() => {
    loadData(
      () =>
        fetchMaterials(
          page,
          pageSize,
          searchTerm,
          sortBy,
          order,
          categoryFilter || undefined
        ),
      showSnackbar
    );
  }, [state, pageSize, searchTerm, page, sortBy, order, categoryFilter]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, categoryFilter]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    let sortBy = newSortModel[0]?.field || 'name';
    const order = (newSortModel[0]?.sort || 'asc').toUpperCase();
    if (sortBy === 'status') {
      sortBy = 'isActive';
    }
    setPage(1);
    setSortBy(sortBy);
    setOrder(order as 'ASC' | 'DESC');
  };

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'code',
      headerName: 'Code',
      flex: 0.75,
    },
    {
      field: 'category',
      headerName: 'Category',
      flex: 0.8,
      sortable: false,
    },
    {
      field: 'unit',
      headerName: 'Unit',
      flex: 0.4,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    {
      field: 'unitPrice',
      headerName: 'Unit Price',
      flex: 0.5,
      headerAlign: 'right',
      align: 'right',
      sortable: false,
    },
    {
      field: 'availableQty',
      headerName: 'Available Quantity',
      flex: 0.75,
      headerAlign: 'right',
      align: 'right',
    },
    {
      field: 'minLevelQty',
      headerName: 'Minimum Quantity Level',
      flex: 1,
      headerAlign: 'right',
      align: 'right',
    },

    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <CenteredCellBox>
          <Status status={params.value} />
        </CenteredCellBox>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <RawMaterialActionMenuButton
          rowId={params.id}
          code={params.row.code}
          status={params.row.status}
          availableQty={params.row.availableQty}
          fetchMaterials={() =>
            fetchMaterials(
              page,
              pageSize,
              searchTerm,
              sortBy,
              order,
              categoryFilter || undefined
            )
          }
          deleteMaterial={deleteMaterial}
        />
      ),
    },
  ];

  const transformData = (materials: RawMaterial[]) => {
    try {
      return materials.map((material, index) => ({
        id: material.id,
        number: (page - 1) * pageSize + index + 1,
        name: material.name,
        code: material.code,
        category: material.category || '-',
        availableQty: new Intl.NumberFormat('en-US').format(
          Number(material.availableQty)
        ),
        minLevelQty: new Intl.NumberFormat('en-US').format(
          Number(material.minLevelQty)
        ),
        unit: material.unit,
        unitPrice: new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(material.unitPrice)),
        status: material.isActive ? 'Active' : 'Inactive',
      }));
    } catch (error) {
      showSnackbar('Error transforming material data', 'error');
      return [];
    }
  };

  const rows = transformData(materials);

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={rows}
        columns={columns}
        disableColumnMenu
        sortModel={sortModel}
        sortingMode="server"
        sortingOrder={['asc', 'desc']}
        onSortModelChange={handleSortModelChange}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
      />
    </StyledGridContainer>
  );
};

export default RawMaterialGrid;
